import {Box} from '@mui/material';
import {useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  useParams,
  useRouteMatch,
  useHistory,
  Redirect,
  Route,
  Switch,
  matchPath,
} from 'react-router-dom';
import {DeviceType} from '../../backend/enums/device-type';
import {AppContext} from '../../contexts/app/context';
import {AppContextType} from '../../contexts/app/types';
import {OfficeContext} from '../../contexts/office/context';
import {OfficeContextType} from '../../contexts/office/types';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {translations} from '../../i18n/translations';
import {ContentPage} from '../content-page/content-page';
import {SectionTabs} from '../section-tabs/section-tabs';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {SubmitButtonState} from '../submit-button/submit-button';
import {DevicesRepository} from '../../repositories/devices';
import DeviceDetailsProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/devices/edit/details/device-details.professional-caregiver.screen';
import DeviceHealthProfessionalCaregiverOfficeScreen from '../../screens/private/office/professional-caregiver/devices/edit/health/device-health.professional-caregiver.screen';
import DeviceDetailsSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/devices/edit/details/device-details.system-administrator.screen';
import DeviceHealthSystemAdministratorOfficeScreen from '../../screens/private/office/system-administrator/devices/edit/health/device-health.system-administrator.screen';

enum DeviceSectionTabs {
  Details = 'details',
  Health = 'health',
}

enum PathExtensions {
  Health = 'health',
}

/**
 * Device navigation tabs used to navigate between the device's screen.
 *
 * Permissions are given by props.
 */
export const DeviceTabs = (props: {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite?: boolean;
}) => {
  // Get params
  let {deviceId}: {deviceId?: string} = useParams();
  if (deviceId?.toLowerCase() === 'create') {
    deviceId = undefined;
  }

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const appContext: AppContextType = useContext(AppContext);

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  // Get current tab based on the locations we are
  const handleGetCurrentTab = (pathname: string) => {
    if (
      matchPath(pathname, {
        path: `${path}/${PathExtensions.Health}`,
        exact: true,
        strict: false,
      })
    ) {
      return DeviceSectionTabs.Health;
    }

    // Default
    return DeviceSectionTabs.Details;
  };

  // Hold state
  const [currentTab, setCurrentTab] = useState<DeviceSectionTabs>(
    handleGetCurrentTab(window.location.pathname)
  );

  // Listen for route changes
  useEffect(() => {
    // Listen for route changes
    const routeChangesUnsubscribe = history.listen((location: any) => {
      // Let's update the current tab
      setCurrentTab(handleGetCurrentTab(location.pathname));
    });

    // Clean up
    return () => {
      // Unsubscribe
      routeChangesUnsubscribe();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, path]);

  // Validate and get the device
  useEffect(() => {
    const getDevice = async () => {
      if (deviceId) {
        try {
          // Get device
          const result = await DevicesRepository.instance.getById(deviceId);

          if (result) {
            // Edit device
            officeContext.setDeviceForm(result);
          } else {
            // Error
            throw new Error('Empty device.');
          }
        } catch (error) {
          // Log
          console.error('🔴 Could not get device.', error);

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_tabs.errors.device_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${deviceId}`, '')}`);
        }
      } else {
        if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
          // Create device
          officeContext.setDeviceForm({
            product: appContext.state.configuration.product,
            isActive: true,
            type: DeviceType.Unknown,
          });
        } else {
          // Log
          console.error('🔴 Could not get device.');

          // Show toast error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.device_tabs.errors.device_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace('/create', '')}`);
        }
      }
    };

    // Get device
    getDevice();

    // Clean up
    return () => {
      // Clear device
      officeContext.clearDeviceForm();

      // Log
      console.debug('🧹 Device form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  return (
    <ContentPage>
      {deviceId && (
        <Box sx={{marginBottom: '16px'}}>
          <SectionTabs
            title={
              officeContext?.state?.deviceForm &&
              t(
                translations.device_types[officeContext.state.deviceForm.type ?? DeviceType.Unknown]
              ) +
                ' ' +
                (officeContext.state.deviceForm.serialNumber ??
                  officeContext.state.deviceForm.macAddress)
            }
            currentTab={currentTab}
            tabs={[
              {
                value: DeviceSectionTabs.Details,
                label: t(translations.device_tabs.tabs.details),
                href: url,
              },
              {
                value: DeviceSectionTabs.Health,
                label: t(translations.device_tabs.tabs.information),
                href:
                  url.charAt(url.length - 1) === '/'
                    ? `${url}${PathExtensions.Health}`
                    : `${url}/${PathExtensions.Health}`,
              },
            ]}
            exitUrl={`${url.replace(`/${deviceId}`, '')}`}
            disabled={
              officeContext.state.deviceFormDeleteButtonState === SubmitButtonState.Loading ||
              officeContext.state.deviceFormSubmitButtonState === SubmitButtonState.Loading
            }
          />
        </Box>
      )}
      {/* Main content */}
      <Box>
        <Switch>
          {/* System admin - Edit or create device */}
          {props.caregiverMode === CaregiverMode.SystemAdministrator && (
            <Route exact path={path}>
              <DeviceDetailsSystemAdministratorOfficeScreen />
            </Route>
          )}
          {/* System admin - Device health */}
          {props.caregiverMode === CaregiverMode.SystemAdministrator && (
            <Route exact path={`${path}/${PathExtensions.Health}`}>
              <DeviceHealthSystemAdministratorOfficeScreen />
            </Route>
          )}
          {/* Professional caregiver - View/edit device */}
          {props.caregiverMode === CaregiverMode.Professional && (
            <Route exact path={path}>
              <DeviceDetailsProfessionalCaregiverOfficeScreen canWrite={props.canWrite ?? false} />
            </Route>
          )}
          {/* Professional caregiver - Device health */}
          {props.caregiverMode === CaregiverMode.Professional && (
            <Route exact path={`${path}/${PathExtensions.Health}`}>
              <DeviceHealthProfessionalCaregiverOfficeScreen canWrite={props.canWrite ?? false} />
            </Route>
          )}

          {/** Default Route */}
          <Route path={path}>
            <Redirect to={url} />
          </Route>
        </Switch>
      </Box>
    </ContentPage>
  );
};
